import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { connect } from 'react-redux';
import usePortal from 'react-cool-portal';
import EditIcon from '@stripcash/ui-kit/icons/edit.svg';
import Appear from '@stripcash/ui-kit/components/Motion/Appear';

import HeaderButton from 'common/components/Header/components/HeaderButton';
import { Translate } from 'common/i18n/types';
import {
  ContactUsStateType,
  hideModalContactUs,
  isFetchingContactUsSelector,
  ModalsContactUs,
  showModalContactUsSelector,
  showModalWithMessageContactUs,
} from 'affiliates/reducers/contactUs';
import sendMessageContactUs from 'affiliates/actions/contactUs';

import style from './style.module.css';
import ModalWithMessage from './components/modals/ModalWithMessage';
import ModalThank from './components/modals/ModalThank';
import { CONTACT_US_ID } from './constants';

type ContactUsProps = {
  isLoading: boolean;
  showModal: ModalsContactUs;
  hideModalContactUsAction: () => void;
  sendMessageContactUsAction: (message: string) => void;
  showModalWithMessageContactUsAction: () => void;
  t: Translate;
};

export const ContactUs = ({
  showModal,
  hideModalContactUsAction,
  sendMessageContactUsAction,
  showModalWithMessageContactUsAction,
  isLoading,
  t,
}: ContactUsProps) => {
  const [message, setMessage] = useState<string>('');

  const { Portal, show, hide, isShow } = usePortal({
    containerId: CONTACT_US_ID,
    escToHide: false,
    clickOutsideToHide: true,
    defaultShow: false,
    onHide: () => hideModalContactUsAction(),
  });

  const handleOpenModal = useCallback(() => {
    showModalWithMessageContactUsAction();
    show();
  }, [show, showModalWithMessageContactUsAction]);

  const handleCloseModal = useCallback(() => {
    hideModalContactUsAction();
    hide();
  }, [hide, hideModalContactUsAction]);

  const handleMessageChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(event.target.value);
    },
    [],
  );

  const handleMessageSend = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();

      sendMessageContactUsAction(message);
    },
    [message, sendMessageContactUsAction],
  );

  useEffect(() => {
    if (showModal === ModalsContactUs.ThankModal) {
      setMessage('');
    }
  }, [showModal]);

  const handleHeaderButtonClick = useCallback(() => {
    if (isShow) {
      handleCloseModal();
    } else {
      handleOpenModal();
    }
  }, [handleCloseModal, handleOpenModal, isShow]);

  return (
    <div id={CONTACT_US_ID}>
      <HeaderButton
        onClick={handleHeaderButtonClick}
        title={t('contactUs.title') as string}
        icon={<EditIcon />}
      />
      <Portal>
        <div className={style.contactUsModalWrapper}>
          <Appear>
            {showModal === ModalsContactUs.MessageModal && (
              <ModalWithMessage
                message={message}
                onMessageChange={handleMessageChange}
                onMessageSend={handleMessageSend}
                onClose={handleCloseModal}
                isLoading={isLoading}
                t={t}
              />
            )}

            {showModal === ModalsContactUs.ThankModal && (
              <ModalThank onClose={handleCloseModal} t={t} />
            )}
          </Appear>
        </div>
      </Portal>
    </div>
  );
};

const mapStateToProps = (state: { contactUs: ContactUsStateType }) => ({
  isLoading: isFetchingContactUsSelector(state),
  showModal: showModalContactUsSelector(state),
});

const mapDispatchToProps = {
  hideModalContactUsAction: hideModalContactUs,
  sendMessageContactUsAction: sendMessageContactUs,
  showModalWithMessageContactUsAction: showModalWithMessageContactUs,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactUs);
